import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-job-notes',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslatePipe],
  template: `
    <div class="min-h-screen flex flex-col pb-24" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-40 text-white shadow-sm flex items-center gap-3"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <a [routerLink]="['/job', jobId()]" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 shrink-0">
          <i class="ti ti-arrow-left text-[20px]"></i>
        </a>
        <div class="flex-1 min-w-0">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ 'jobRecord' | t }}</p>
          <h1 class="text-lg font-semibold text-white/95 truncate">{{ 'jobNotes' | t }}</h1>
        </div>
        <span class="text-[10px] font-medium px-2.5 py-1 rounded-full uppercase border bg-white/10 text-white/75 border-white/15">{{ notes().length }}</span>
      </header>

      <main class="flex-1 px-4 pt-5 space-y-4">
        <!-- Add Note -->
        <div class="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <h2 class="text-xs font-bold text-[#1A6FA6] uppercase tracking-wider mb-3">{{ 'addNote' | t }}</h2>
          <textarea #noteInput rows="4" placeholder="Type your note here..."
            class="w-full p-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] focus:ring-2 focus:ring-sky-100 outline-none transition-all resize-none text-sm font-medium text-slate-800"></textarea>
          <button (click)="submitNote(noteInput)"
            [disabled]="isSaving()"
            class="mt-3 w-full h-12 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]"
            style="background: linear-gradient(135deg,#0F334A,#145B7A);">
            @if (isSaving()) { <i class="ti ti-loader animate-spin"></i> Saving... }
            @else { <i class="ti ti-send"></i> {{ 'addNote' | t }} }
          </button>
        </div>

        <!-- Notes List -->
        @if (isLoading()) {
          <div class="flex items-center justify-center py-16">
            <i class="ti ti-loader animate-spin text-[32px] text-[#2B9AD2]"></i>
          </div>
        } @else if (notes().length === 0) {
          <div class="bg-white rounded-2xl p-10 text-center border border-dashed border-slate-200">
            <i class="ti ti-notes text-[48px] text-slate-300 mb-4 block"></i>
            <h3 class="font-bold text-slate-900 mb-1">{{ 'noNotesYet' | t }}</h3>
            <p class="text-sm text-slate-400">Add the first note for this job.</p>
          </div>
        } @else {
          @for (note of notes(); track note.id) {
            <div class="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <div class="flex items-start justify-between mb-2">
                <div class="flex items-center gap-2">
                  <div class="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">
                    {{ note.author_name?.charAt(0) || '?' }}
                  </div>
                  <div>
                    <p class="text-sm font-bold text-slate-800">{{ note.author_name }}</p>
                    <p class="text-[10px] text-slate-400">{{ note.author_role }}</p>
                  </div>
                </div>
                <p class="text-[10px] text-slate-400 font-medium">{{ note.created_at | date:'d MMM, h:mm a' }}</p>
              </div>
              <p class="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-xl p-3">{{ note.content }}</p>
            </div>
          }
        }
      </main>
    </div>
  `
})
export class JobNotesComponent implements OnInit {
  api = inject(ApiService);
  route = inject(ActivatedRoute);

  jobId = signal('');
  notes = signal<any[]>([]);
  isLoading = signal(true);
  isSaving = signal(false);

  ngOnInit() {
    this.route.paramMap.subscribe((params: any) => {
      const id = params.get('id');
      if (id) { this.jobId.set(id); this.loadNotes(id); }
    });
  }

  loadNotes(id: string) {
    this.isLoading.set(true);
    this.api.getNotes(id).subscribe({
      next: (res: any) => {
        if (res.success) this.notes.set(res.data?.notes || []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  submitNote(textarea: HTMLTextAreaElement) {
    const content = textarea.value.trim();
    if (!content) return;
    this.isSaving.set(true);
    this.api.addNote({ reference_id: this.jobId(), model_type: 'Job', content }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          textarea.value = '';
          showToast('success', 'Note saved.');
          this.loadNotes(this.jobId());
        } else { showToast('error', res.message || 'Failed to save note.'); }
      },
      error: () => { this.isSaving.set(false); showToast('error', 'Network error.'); }
    });
  }
}
